import {
  FormData,
  FormComponent,
  COMPONENT_TYPE_CHECKBOX,
  COMPONENT_TYPE_RADIOBUTTON,
  COMPONENT_TYPE_GUEST,
  OptionInfo,
  validationFieldsGuest,
  Guests,
  GuestInfo,
} from '@entities/leadCollector';
import { REQUIRED_FIELD_MESSAGE } from '@shared/constants/errorMessages';

const validateCheckbox = (component: FormComponent): void => {
  const options = component.properties.options as OptionInfo[];

  for (const option of options) {
    option.error = undefined;
  }

  for (const option of options) {
    if (!option.isActive) {
      option.error = REQUIRED_FIELD_MESSAGE;
    }
  }
};

const validateRadiobutton = (component: FormComponent): void => {
  const options = component.properties.options as OptionInfo[];

  for (const option of options) {
    option.error = undefined;
  }

  const hasSelected = options.some((option) => option.isActive);
  if (hasSelected) return;

  for (const option of options) {
    option.error = REQUIRED_FIELD_MESSAGE;
  }
};

const validateGuest = async (component: FormComponent): Promise<void> => {
  const guests = component.properties as unknown as Guests;
  await validationFieldsGuest(guests.mainGuest);
  if (guests.adultGuest) {
    for (const guest of guests.adultGuest) {
      await validationFieldsGuest(guest);
    }
  }
  if (guests.childrenGuest) {
    for (const guest of guests.childrenGuest) {
      await validationFieldsGuest(guest);
    }
  }
};

const hasGuestError = (guest: GuestInfo): boolean => {
  const hasFIOError = guest.FIO.some((field) => field.error);
  const hasAdditionalFieldsError = guest.additionalFields.some((field) => field.error);
  return hasFIOError || hasAdditionalFieldsError;
};

const validators: Record<string, (component: FormComponent) => void | Promise<void>> = {
  [COMPONENT_TYPE_CHECKBOX]: validateCheckbox,
  [COMPONENT_TYPE_RADIOBUTTON]: validateRadiobutton,
  [COMPONENT_TYPE_GUEST]: validateGuest,
};

export const useFormValidation = () => {
  const validateForm = async (formData: FormData): Promise<boolean> => {
    const components = formData.components;

    for (const component of components) {
      const validator = validators[component.type];
      if (validator) {
        await validator(component);
      }
    }

    for (const component of components) {
      if (component.type === COMPONENT_TYPE_CHECKBOX || component.type === COMPONENT_TYPE_RADIOBUTTON) {
        const options = component.properties.options as OptionInfo[];
        const hasError = options?.some((option) => option.error);
        if (hasError) return false;
      }
      if (component.type === COMPONENT_TYPE_GUEST) {
        const guests = component.properties as unknown as Guests;
        if (hasGuestError(guests.mainGuest)) return false;

        const hasAdultGuestError = guests.adultGuest?.some((guest) => hasGuestError(guest));
        if (hasAdultGuestError) return false;

        const hasChildrenGuestError = guests.childrenGuest?.some((guest) => hasGuestError(guest));
        if (hasChildrenGuestError) return false;
      }
    }

    return true;
  };

  return { validateForm };
};
